import type { IDataTable } from '@shared/models/data-table.model';
import type { Baseline, Project } from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { getBuildingDesignFromProjectOrBuilding } from '../design/design.helpers';
import type { EnvironmentHourData } from '../energy/weather.models';
import { getId } from '../object.helpers';
import { getProjectMeta } from '../project.helpers';

/** Inputs for proposal resolve/enrich shared by client and pipeline. */
export type ResolvedVersionEnrichContext = {
  project: Project;
  weatherHours?: EnvironmentHourData[];
  dataTables?: IDataTable[];
};

/**
 * Enrich inputs for the proposal-visible resolved tree (project, weather, data tables).
 * Returns `undefined` when there is no project so resolve stays on the `no-project` cache entry.
 */
export const buildResolvedVersionEnrichContext = (
  project: Project | undefined,
  extras?: Pick<ResolvedVersionEnrichContext, 'weatherHours' | 'dataTables'>,
): ResolvedVersionEnrichContext | undefined => {
  if (!project) return undefined;
  return {
    project,
    weatherHours: extras?.weatherHours,
    dataTables: extras?.dataTables,
  };
};

/**
 * Accepts a full context or a bare {@link Project} (legacy callers).
 */
export const normalizeResolvedVersionEnrichContext = (
  enrichContext?: ResolvedVersionEnrichContext | Project,
): ResolvedVersionEnrichContext | undefined => {
  if (!enrichContext) return undefined;
  if ('project' in enrichContext) return enrichContext;
  return { project: enrichContext };
};

/**
 * Cache invalidation inputs for project-scoped enrichment (meta/design identity, not `project` ref).
 */
export const getResolvedVersionEnrichCacheDeps = (
  project: Project,
): readonly [
  number,
  ReturnType<typeof getProjectMeta>,
  ReturnType<typeof getBuildingDesignFromProjectOrBuilding>,
] => [
  project.id,
  getProjectMeta(project),
  getBuildingDesignFromProjectOrBuilding(project),
];

/**
 * Stable proposal segment for {@link getResolvedBuildingVersion} cache keys and deps.
 * Uses proposal id only so string ids and proposal objects share one cache entry.
 */
export const getResolvedVersionCacheProposalKey = (
  proposalOrId: ItemOrItemId<IProposal> | undefined,
): string => (proposalOrId ? getId(proposalOrId) : 'baseline');

/**
 * Dependency list for {@link getResolvedBuildingVersion} `cacheFactory`.
 * Proposal is keyed by id; `version` / `version.actions` still invalidate on store updates.
 */
export const getResolvedBuildingVersionCacheDeps = (
  version: Baseline,
  proposalOrId: ItemOrItemId<IProposal> | undefined,
  enrichContext?: ResolvedVersionEnrichContext,
): readonly unknown[] => {
  const enrichDeps = enrichContext?.project
    ? getResolvedVersionEnrichCacheDeps(enrichContext.project)
    : (['no-project'] as const);
  return [
    version,
    version.actions,
    getResolvedVersionCacheProposalKey(proposalOrId),
    ...enrichDeps,
    enrichContext?.weatherHours ?? null,
    enrichContext?.dataTables ?? null,
  ];
};
